import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vcollectSubmissionsApi, SUBMISSION_STATUS_LABELS } from '../../api/vcollect-submission';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

const DECISION_LABELS: Record<string, string> = { flagged: 'Flag', under_review: 'Mark Under Review', verified: 'Verify', returned: 'Return', invalidated: 'Invalidate' };

export function SubmissionWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const submissionId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: subData, isError } = useQuery({ queryKey: ['vcollect-submission', submissionId], queryFn: () => vcollectSubmissionsApi.get(submissionId) });
  const submission = subData?.data;
  const { data: reviewsData } = useQuery({ queryKey: ['vcollect-submission-reviews', submissionId], queryFn: () => vcollectSubmissionsApi.listReviews(submissionId) });
  const reviews = reviewsData?.data ?? [];
  const { data: flagsData } = useQuery({ queryKey: ['vcollect-quality-flags', submissionId], queryFn: () => vcollectSubmissionsApi.listQualityFlags(submissionId) });
  const flags = flagsData?.data ?? [];
  const resolveFlagMutation = useMutation({
    mutationFn: (flagId: number) => vcollectSubmissionsApi.resolveQualityFlag(flagId),
    onSuccess: () => { showSuccess('Flag resolved.'); queryClient.invalidateQueries({ queryKey: ['vcollect-quality-flags', submissionId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  const [comment, setComment] = useState('');
  const reviewMutation = useMutation({
    mutationFn: (decision: string) => vcollectSubmissionsApi.review(submissionId, decision, comment || undefined),
    onSuccess: () => {
      showSuccess('Review recorded.');
      setComment('');
      queryClient.invalidateQueries({ queryKey: ['vcollect-submission', submissionId] });
      queryClient.invalidateQueries({ queryKey: ['vcollect-submission-reviews', submissionId] });
    },
    onError: (err: ApiError) => showError(err.message),
  });

  if (isError) return <div className="page"><div className="error-state">Failed to load this submission. Please try refreshing the page.</div></div>;
  if (!submission) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  return (
    <div className="page">
      <RecordHeader
        title={submission.submission_reference}
        readOnly={true}
        entityType="vcollect_submission"
        entityId={submissionId}
        fields={[
          { label: 'Enumerator', value: submission.enumerator_name ?? 'Unknown' },
          { label: 'Submitted', value: submission.submitted_at ? formatDate(submission.submitted_at) : '-' },
          { label: 'Received', value: formatDate(submission.received_at) },
          { label: 'Status', value: <StatusBadge status={submission.review_status} label={SUBMISSION_STATUS_LABELS[submission.review_status]} /> },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'responses', label: 'Responses', content: (
              <div>
                <SectionHeader title="Raw Submission Data" />
                <p className="muted" style={{ fontSize: 12 }}>Shown as the raw ODK submission XML - a human-readable, labeled view is a future enhancement. The original data is never altered.</p>
                <pre style={{ background: 'var(--color-page-bg)', padding: 12, borderRadius: 4, maxHeight: 400, overflow: 'auto', fontSize: 11 }}>{submission.raw_xml}</pre>
              </div>
            ),
          },
          {
            key: 'quality', label: `Quality${flags.filter((f) => !f.resolved).length > 0 ? ` (${flags.filter((f) => !f.resolved).length})` : ''}`, content: (
              <div>
                <SectionHeader title="Automated Quality Flags" />
                <p className="muted" style={{ fontSize: 12 }}>A flag is an automated observation, not a review decision - resolving it does not change the submission's review status.</p>
                <ul>
                  {flags.map((f) => (
                    <li key={f.id} style={{ marginBottom: 8, opacity: f.resolved ? 0.5 : 1 }}>
                      {f.message}
                      {f.resolved ? <span className="muted"> (resolved)</span> : (
                        <button className="btn-secondary" style={{ marginLeft: 8 }} onClick={() => resolveFlagMutation.mutate(f.id)} disabled={resolveFlagMutation.isPending}>Resolve</button>
                      )}
                    </li>
                  ))}
                  {flags.length === 0 && <li className="muted">No quality flags on this submission.</li>}
                </ul>
              </div>
            ),
          },
          {
            key: 'review', label: 'Review', content: (
              <div>
                <SectionHeader title="Record a Review Decision" />
                <textarea placeholder="Comment (required for Return / Invalidate)" value={comment} onChange={(e) => setComment(e.target.value)} style={{ width: '100%', minHeight: 70, marginBottom: 10 }} />
                <div className="form-actions">
                  {Object.entries(DECISION_LABELS).map(([decision, label]) => (
                    <button key={decision} className={decision === 'verified' ? '' : 'btn-secondary'} onClick={() => reviewMutation.mutate(decision)} disabled={reviewMutation.isPending}>{label}</button>
                  ))}
                </div>

                <SectionHeader title="Review History" />
                <ul>
                  {reviews.map((r) => (
                    <li key={r.id} style={{ marginBottom: 6 }}>
                      <b>{SUBMISSION_STATUS_LABELS[r.decision] ?? r.decision}</b> — {formatDate(r.reviewed_at)}
                      {r.comment && <div className="muted">{r.comment}</div>}
                    </li>
                  ))}
                  {reviews.length === 0 && <li className="muted">No review decisions recorded yet.</li>}
                </ul>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
